import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { LayoutTemplate, Plus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { TextField } from '@/components/form-fields';
import { EmptyState, ReadOnlyNotice, SectionCard } from '@/components/settings/settings-kit';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/lib/auth';
import { createDesign, deleteDesign, listDesigns, publishDesign } from '@/lib/designs';

/**
 * Designs.
 *
 * One design is live and the rest are drafts, so a redesign can be built
 * next to the running store and swapped in when it is ready. The live one
 * cannot be deleted -- deleting it would take the storefront's chrome with
 * it -- so publishing another first is the only way past that.
 */
export const Route = createFileRoute('/designs/')({
  component: DesignsPage,
});

function DesignsPage() {
  const { activeAccountId, can } = useAuth();
  const accountId = activeAccountId ?? '';
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const canManage = can('cms.manage');

  const [name, setName] = useState('');

  const designs = useQuery({
    queryKey: ['designs', accountId],
    queryFn: () => listDesigns(accountId),
    enabled: Boolean(accountId),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['designs', accountId] });

  const create = useMutation({
    mutationFn: () => createDesign(accountId, name.trim()),
    onSuccess: (design) => {
      setName('');
      refresh();
      navigate({ to: '/designs/$siteId', params: { siteId: design.id } });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const publish = useMutation({
    mutationFn: (siteId: string) => publishDesign(accountId, siteId),
    onSuccess: (design) => {
      toast.success(`${design.name} is live.`);
      refresh();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const remove = useMutation({
    mutationFn: (siteId: string) => deleteDesign(accountId, siteId),
    onSuccess: () => {
      toast.success('Design deleted.');
      refresh();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Designs</h1>
        <p className="text-muted-foreground text-sm">
          The theme and the header and footer every page is framed with.
        </p>
      </div>

      {!canManage && <ReadOnlyNotice permission="cms.manage" />}

      <SectionCard
        title="Your designs"
        description="One is live. The others are drafts nobody outside this console can see."
      >
        {designs.isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : designs.data?.length ? (
          <ul className="divide-y rounded-lg border">
            {designs.data.map((design) => (
              <li key={design.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <Link
                    to="/designs/$siteId"
                    params={{ siteId: design.id }}
                    className="font-medium hover:underline"
                  >
                    {design.name}
                  </Link>
                  <p className="text-muted-foreground text-xs">
                    {design.updatedAt
                      ? `Edited ${new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(
                          new Date(design.updatedAt),
                        )}`
                      : 'Never edited'}
                  </p>
                </div>

                {design.published && <Badge>Live</Badge>}

                {canManage && !design.published && (
                  <>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => publish.mutate(design.id)}
                      disabled={publish.isPending}
                    >
                      Make live
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => remove.mutate(design.id)}
                      disabled={remove.isPending}
                    >
                      Delete
                    </Button>
                  </>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState>
            <LayoutTemplate className="mx-auto mb-2 size-6" />
            No designs yet.
          </EmptyState>
        )}

        {canManage && (
          <div className="flex flex-wrap items-end gap-3">
            <TextField
              id="design-name"
              label="New design"
              className="w-56"
              placeholder="Spring refresh"
              value={name}
              onChange={setName}
            />
            <Button
              type="button"
              onClick={() => create.mutate()}
              disabled={!name.trim() || create.isPending}
            >
              <Plus className="size-4" />
              {create.isPending ? 'Creating…' : 'Create'}
            </Button>
          </div>
        )}
      </SectionCard>
    </div>
  );
}
